import { useEscape } from "@/hooks/useKeyboardShortcut";
import { useOutsideClick } from "@/hooks/useOutsideClick";
import { cn } from "@/lib/utils";
import { BREAKPOINTS } from "@/settings";
import {
  ReactNode,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useMediaQuery } from "usehooks-ts";

import { BottomSheetView } from "../BottomSheet/BottomSheetView";
import { Button } from "../Button/Button";
import CloseButton from "../SpecificButtons/CloseButton/CloseButton";

type Placement = "bottom" | "top" | "left" | "right";
type Align = "start" | "center" | "end";

// Minimum distance the panel keeps from the viewport edges.
const VIEWPORT_MARGIN = 12;
// Gap between the trigger and the panel along the placement axis.
const TRIGGER_GAP = 10;

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), Math.max(min, max));

export type PopoverProps = {
  /** Label of the built-in secondary trigger button. Ignored when `trigger` is set. */
  triggerLabel?: ReactNode;
  /**
   * Custom trigger element (icon button, tag, …) rendered instead of the
   * built-in button. As a render prop it is handed the open state, for a
   * trigger that shows it — a caret that flips, say.
   */
  trigger?: ReactNode | ((open: boolean) => ReactNode);
  /** Omit for a bare panel — the close button then floats in the top-right corner. */
  title?: string;
  /** Body content, or a render prop for content that closes the panel itself. */
  children: ReactNode | ((close: () => void) => ReactNode);
  /** Empty string hides the action; both hidden hides the row (info popover). */
  negLabel?: string;
  posLabel?: string;
  onNegative?: () => void;
  onPositive?: () => void;
  placement?: Placement;
  /** Which edge of the trigger the panel aligns to along the placement axis. */
  align?: Align;
  width?: number;
  /**
   * Paint the panel on the 2.0 neutral ramp. `bg-panel-base` is the 1.0
   * slate; a panel whose contents use neutral-* looks tinted on it.
   */
  newDesignSystem?: boolean;
  /**
   * Tailwind padding class reserving the anchored ad height under the touch
   * sheet. Supplied by the connected Popover wrapper; omitted here so this
   * module stays free of Redux.
   */
  sheetPaddingBottomClassName?: string;
};

/**
 * Compact panel anchored to its own trigger. Closes on outside click, Escape,
 * the X, or either action (after its callback).
 *
 * The panel is rendered in a portal with fixed positioning so it is never
 * clipped by a scrolling or overflow-hidden ancestor, and it is clamped to a
 * 12px viewport margin on both axes — wide panels (e.g. width 760) stay fully
 * visible no matter where the trigger sits. On touch devices (max-width
 * 1025px) it is presented as a BottomSheet instead of an anchored panel.
 */
export const Popover = ({
  triggerLabel,
  trigger: customTrigger,
  title,
  children,
  negLabel = "Cancel",
  posLabel = "Confirm",
  onNegative,
  onPositive,
  placement = "bottom",
  align = "start",
  width = 280,
  newDesignSystem = false,
  sheetPaddingBottomClassName,
}: PopoverProps) => {
  const surface = newDesignSystem ? "bg-neutral-900" : "bg-panel-base";
  const [open, setOpen] = useState(false);
  /* Stable: useOutsideClick and useEscape both key their effect on the
     callback, so a fresh arrow would re-register document listeners on every
     render — and a screen can hold dozens of popovers. */
  const close = useCallback(() => setOpen(false), []);
  const [panelPos, setPanelPos] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const ref = useOutsideClick(close, open);
  const isTouchDevice = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);
  useEscape(close, open);

  // Position the fixed panel against the trigger, flipping to the opposite
  // side when the preferred one has no room, then clamp inside the viewport.
  useLayoutEffect(() => {
    if (!open || isTouchDevice) {
      setPanelPos(null);
      return;
    }
    const compute = () => {
      const anchor = ref.current as HTMLElement | null;
      const panel = panelRef.current;
      if (!anchor || !panel) return;
      const rect = anchor.getBoundingClientRect();
      const panelWidth = Math.min(
        width,
        window.innerWidth - VIEWPORT_MARGIN * 2,
      );
      const panelHeight = panel.offsetHeight;
      const maxTop = window.innerHeight - VIEWPORT_MARGIN - panelHeight;
      let left: number;
      let top: number;

      if (placement === "bottom" || placement === "top") {
        left =
          align === "center"
            ? rect.left + (rect.width - panelWidth) / 2
            : align === "end"
              ? rect.right - panelWidth
              : rect.left;
        const below = rect.bottom + TRIGGER_GAP;
        const above = rect.top - TRIGGER_GAP - panelHeight;
        if (placement === "bottom") {
          top = below > maxTop && above >= VIEWPORT_MARGIN ? above : below;
        } else {
          top = above < VIEWPORT_MARGIN && below <= maxTop ? below : above;
        }
      } else {
        left =
          placement === "right"
            ? rect.right + TRIGGER_GAP
            : rect.left - TRIGGER_GAP - panelWidth;
        top =
          align === "start"
            ? rect.top
            : align === "end"
              ? rect.bottom - panelHeight
              : rect.top + (rect.height - panelHeight) / 2;
      }

      setPanelPos({
        left: clamp(
          left,
          VIEWPORT_MARGIN,
          window.innerWidth - VIEWPORT_MARGIN - panelWidth,
        ),
        top: clamp(top, VIEWPORT_MARGIN, maxTop),
      });
    };
    compute();
    window.addEventListener("resize", compute);
    // Capture phase so scrolling any ancestor container repositions the panel.
    window.addEventListener("scroll", compute, true);
    return () => {
      window.removeEventListener("resize", compute);
      window.removeEventListener("scroll", compute, true);
    };
  }, [open, isTouchDevice, placement, align, width, ref]);

  const hasNeg = negLabel.trim() !== "";
  const hasPos = posLabel.trim() !== "";

  const toggle = () => setOpen((prev) => !prev);

  const triggerNode =
    typeof customTrigger === "function" ? customTrigger(open) : customTrigger;

  const trigger = triggerNode ? (
    // `contents` keeps the custom element's own layout; the click is caught as
    // it bubbles out of it.
    <span className="contents" onClick={toggle}>
      {triggerNode}
    </span>
  ) : (
    <Button variant="secondary" size="medium" onClick={toggle}>
      {triggerLabel}
    </Button>
  );

  // Without a title there is no header row to sit in, so the close button is
  // pulled out of the flow and pinned to the panel's top-right corner.
  const header = title ? (
    <div className="mb-2.5 flex items-start justify-between gap-3">
      <div className="text-[15px] font-semibold leading-[1.3] text-foreground">
        {title}
      </div>
      <div className="-mr-1 -mt-1 shrink-0">
        <CloseButton onClick={close} />
      </div>
    </div>
  ) : (
    <div className="absolute right-3 top-3 z-10">
      <CloseButton onClick={close} />
    </div>
  );

  const body = open ? (
    <div className="text-[13px] leading-[1.55] text-foreground-muted">
      {typeof children === "function" ? children(close) : children}
    </div>
  ) : null;

  const actions = (hasNeg || hasPos) && (
    <div className="mt-4 flex justify-end gap-2">
      {hasNeg && (
        <Button
          variant="secondary"
          size="small"
          onClick={() => {
            onNegative?.();
            close();
          }}
        >
          {negLabel}
        </Button>
      )}
      {hasPos && (
        <Button
          variant="primary"
          size="small"
          onClick={() => {
            onPositive?.();
            close();
          }}
        >
          {posLabel}
        </Button>
      )}
    </div>
  );

  if (isTouchDevice) {
    return (
      <>
        {trigger}
        {/* Portalled for the same reasons as the anchored panel below, plus:
            the sheet is `fixed inset-0`, and a transformed ancestor (any
            enclosing BottomSheet) would otherwise become its containing block
            and trap it inside that sheet. */}
        {open &&
          createPortal(
            <div data-outside-click-ignore>
              <BottomSheetView
                isOpen={open}
                onClose={close}
                className={cn("max-h-[85dvh]", surface)}
                paddingBottomClassName={sheetPaddingBottomClassName}
              >
                {/* relative: anchors the titleless close button to the sheet. */}
                <div className="relative flex flex-col px-5 pb-4 pt-3">
                  {header}
                  {body}
                  {actions}
                </div>
              </BottomSheetView>
            </div>,
            document.body,
          )}
      </>
    );
  }

  return (
    <div className="relative inline-block" ref={ref}>
      {trigger}
      {open &&
        createPortal(
          <div
            ref={panelRef}
            // The panel lives outside the trigger's subtree, so outside-click
            // handlers up the tree must not treat clicks in it as "outside".
            data-outside-click-ignore
            style={{
              width,
              maxWidth: `calc(100vw - ${VIEWPORT_MARGIN * 2}px)`,
              top: panelPos?.top ?? 0,
              left: panelPos?.left ?? 0,
              // Hidden for the single frame before the layout effect measures.
              visibility: panelPos ? "visible" : "hidden",
            }}
            className={cn(
              // Above modals (z-100) so a popover opened inside one is visible.
              "fixed z-[110] max-h-[calc(100dvh-24px)] overflow-y-auto",
              "rounded-[14px] border border-neutral-700 p-4",
              surface,
              "shadow-[0_18px_44px_rgba(0,0,0,0.55)]",
              // Only opacity/transform transition — top/left must not animate,
              // they are rewritten on every scroll frame.
              "transition-[opacity,transform] duration-150 ease-out",
              "translate-y-0 scale-100 opacity-100",
              "starting:-translate-y-1 starting:scale-[0.98] starting:opacity-0",
            )}
          >
            {header}
            {body}
            {actions}
          </div>,
          document.body,
        )}
    </div>
  );
};
